'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import type { LearningStreak } from '@/app/track/utils/streakTypes';
import { EMPTY_STREAK } from '@/app/track/utils/streakTypes';
import { fetchLearningStreak, STREAK_UPDATED_EVENT } from '@/utils/learningStreak';

export function useLearningStreak() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const accessToken = (session?.user as { accessToken?: string } | undefined)?.accessToken;
  const [streak, setStreak] = useState<LearningStreak>(EMPTY_STREAK);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!accessToken) {
      setStreak(EMPTY_STREAK);
      setLoading(false);
      return;
    }

    try {
      const data = await fetchLearningStreak(accessToken);
      setStreak(data);
    } catch {
      setStreak(EMPTY_STREAK);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (status === 'loading') return;
    void reload();
  }, [status, reload, pathname]);

  useEffect(() => {
    const onStreakUpdated = (event: Event) => {
      const detail = (event as CustomEvent<LearningStreak>).detail;
      if (detail) {
        setStreak(detail);
        setLoading(false);
      }
    };

    window.addEventListener(STREAK_UPDATED_EVENT, onStreakUpdated);
    return () => window.removeEventListener(STREAK_UPDATED_EVENT, onStreakUpdated);
  }, []);

  return { streak, loading, reload };
}
